import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, HeartHandshake, CalendarCheck, Sparkles, Crown, Check } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";

const UsageRow = ({ Icon, label, used, limit }) => (
  <div className="flex items-center justify-between gap-3 bg-cream rounded-xl px-4 py-3">
    <div className="flex items-center gap-2.5 text-sm text-teal-deep font-semibold">
      <Icon size={16} className="text-teal" /> {label}
    </div>
    <span className={`text-sm font-bold ${limit != null && used >= limit ? "text-orange" : "text-teal-deep"}`}>
      {used}{limit != null ? ` / ${limit}` : ""}
    </span>
  </div>
);

const ClientDashboard = () => {
  const { t } = useI18n();
  const c = t.community;
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [volunteer, setVolunteer] = useState(false);
  const [limits, setLimits] = useState(null);
  const [interestSent, setInterestSent] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  useEffect(() => { if (user) setVolunteer(!!user.volunteer); }, [user]);

  useEffect(() => {
    if (user && user.role === "client") {
      api.get("/community/limits").then(({ data }) => setLimits(data)).catch(() => {});
    }
  }, [user]);

  const toggleVolunteer = async (v) => {
    setVolunteer(v);
    try {
      await api.put("/me/volunteer", { volunteer: v });
      if (user) setUser({ ...user, volunteer: v });
    } catch (err) { console.error(err); }
  };

  const sendInterest = async () => {
    try {
      await api.post("/community/premium-interest");
      setInterestSent(true);
    } catch (err) { console.error(err); }
  };

  if (!user) return <div className="container-tight py-24 text-center text-teal-soft">…</div>;

  return (
    <section className="bg-cream min-h-screen py-12">
      <div className="container-tight max-w-3xl">
        <div className="bg-white rounded-3xl border border-gray-200 p-8 md:p-10">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="eyebrow text-orange">{t.dashboard.welcome}</p>
              <h1 className="font-display text-4xl text-teal-deep mt-1 leading-tight">{user.full_name || user.email}</h1>
              <p className="text-teal-soft text-sm mt-1">{user.email || user.phone}</p>
              {limits?.premium && (
                <span className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-bold uppercase tracking-wider bg-orange/10 text-orange rounded-full px-3 py-1" data-testid="premium-badge">
                  <Crown size={11} /> {c.premiumActive}
                </span>
              )}
            </div>
            <button onClick={async () => { await logout(); navigate("/"); }} className="text-red-600 hover:text-red-700 text-sm font-semibold inline-flex items-center gap-2">
              <LogOut size={14} /> {t.nav.logout}
            </button>
          </div>

          {limits && (
            <div className="mt-8" data-testid="community-usage">
              <h2 className="text-xs font-bold uppercase tracking-wider text-teal">{c.usageTitle}</h2>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <UsageRow Icon={CalendarCheck} label={c.usageAppointments} used={limits.appointments_used} limit={limits.appointments_limit} />
                <UsageRow Icon={Sparkles} label={c.usageEvents} used={limits.events_used} limit={limits.events_limit} />
              </div>
            </div>
          )}

          <div className="mt-8">
            <label className="flex items-start gap-3 p-5 rounded-2xl border-2 border-teal/20 hover:border-teal/50 cursor-pointer transition-colors bg-teal/5" data-testid="client-d-volunteer">
              <input
                type="checkbox"
                checked={volunteer}
                onChange={(e) => toggleVolunteer(e.target.checked)}
                className="mt-1 w-5 h-5 accent-orange"
              />
              <div className="flex items-start gap-3">
                <HeartHandshake className="text-orange shrink-0 mt-0.5" size={22} />
                <div>
                  <div className="text-base font-bold text-teal-deep">{t.auth.volunteerLabel}</div>
                  <div className="text-xs text-teal-soft mt-1">{t.auth.volunteerHint}</div>
                </div>
              </div>
            </label>
          </div>

          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to="/directory" className="btn-orange">{t.dashboard.goDirectory}</Link>
            <Link to="/red-apoyo/mis-citas" className="btn-teal-outline" data-testid="client-my-bookings">{c.myBookings}</Link>
          </div>

          {limits && limits.is_free_community && (
            <div className="mt-10 rounded-2xl border border-gray-200 bg-cream/60 p-5" data-testid="premium-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-orange/10 text-orange flex items-center justify-center"><Crown size={15} /></span>
                  <div>
                    <span className="text-sm font-bold text-teal-deep">{c.premiumTitle}</span>
                    <span className="text-xs text-teal-soft ml-2">{c.premiumPrice}</span>
                  </div>
                </div>
              </div>
              <ul className="mt-3 space-y-1.5">
                {[c.premiumB1, c.premiumB2, c.premiumB3].map((b) => (
                  <li key={b} className="flex items-center gap-2 text-xs text-teal-soft">
                    <Check size={12} className="text-teal shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              {interestSent ? (
                <p className="mt-4 text-xs font-semibold text-teal" data-testid="premium-thanks">{c.premiumThanks}</p>
              ) : (
                <button onClick={sendInterest} className="mt-4 text-xs font-bold text-orange hover:underline" data-testid="premium-interest-btn">
                  {c.premiumCta} →
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ClientDashboard;
